import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Undo2, RefreshCw, FileText, User } from 'lucide-react';

const TYPE_LABELS = {
  supplier_bill: 'فاتورة مورد',
  supplier_credit_note: 'إشعار دائن مورد',
  expense: 'مصروف',
  'petty_cash.expense': 'مصروف نثرية',
  expense_report: 'تقرير مصروفات',
  invoice: 'فاتورة عميل',
  customer_invoice: 'فاتورة عميل',
  payment: 'دفعة',
};

const fmtDate = (v) => {
  if (!v) return '—';
  const s = String(v);
  return s.length > 10 ? s.slice(0, 10) : s;
};

export default function CancellationsLog() {
  const [data, setData] = useState({ count: 0, total_amount: 0, rows: [] });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/journals/reversals');
      setData(res.data);
    } catch (e) {
      toast.error('فشل تحميل سجل الإلغاءات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  return (
    <div dir="rtl" className="space-y-6" data-testid="cancellations-log-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Undo2 className="w-6 h-6 text-red-600" />
            سجل الإلغاءات
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            كل قيد عكسي تم إنشاؤه عند إلغاء مستند — السبب والتاريخ والمنفذ. القيد الأصلي يبقى محفوظاً في الدفتر ويتصفّر رياضياً.
          </p>
        </div>
        <button
          onClick={fetchData}
          data-testid="cancellations-refresh-btn"
          className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          تحديث
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="cancellations-count-card">
          <p className="text-xs text-gray-500 uppercase">عدد القيود العكسية</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{data.count}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="cancellations-total-card">
          <p className="text-xs text-gray-500 uppercase">إجمالي المبالغ المعكوسة</p>
          <p className="text-2xl font-bold text-red-600 mt-1">
            {Number(data.total_amount || 0).toLocaleString()} SAR
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cancellations-table">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3 text-right">القيد العكسي</th>
                <th className="px-4 py-3 text-right">القيد الأصلي</th>
                <th className="px-4 py-3 text-right">نوع المستند</th>
                <th className="px-4 py-3 text-right">رقم المستند</th>
                <th className="px-4 py-3 text-left">المبلغ</th>
                <th className="px-4 py-3 text-right">السبب</th>
                <th className="px-4 py-3 text-right">المنفذ</th>
                <th className="px-4 py-3 text-right">التاريخ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan="8" className="px-4 py-12 text-center text-gray-400">جارٍ التحميل…</td></tr>
              ) : data.rows.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-12 text-center text-gray-500" data-testid="cancellations-empty">
                    لا توجد قيود عكسية — لم يتم إلغاء أي مستند بعد
                  </td>
                </tr>
              ) : (
                data.rows.map((r) => (
                  <tr key={r.journal_id} className="hover:bg-red-50/40 transition-colors" data-testid={`cancellation-row-${r.journal_no}`}>
                    <td className="px-4 py-3 font-semibold text-red-700">{r.journal_no}</td>
                    <td className="px-4 py-3 text-gray-700">{r.original_journal_no || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                        {TYPE_LABELS[r.source_type] || r.source_type || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-blue-700 font-medium">
                      <span className="inline-flex items-center gap-1">
                        <FileText className="w-3.5 h-3.5" />
                        {r.doc_no || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-left font-semibold">{Number(r.amount || 0).toLocaleString()} SAR</td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs truncate" title={r.reason || ''}>{r.reason || '—'}</td>
                    <td className="px-4 py-3 text-gray-700">
                      <span className="inline-flex items-center gap-1">
                        <User className="w-3.5 h-3.5 text-gray-400" />
                        {r.executed_by}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(r.created_at || r.journal_date)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
